import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Runbook {
  id: string
  name: string
  description: string
  category: string
  steps: number
  estimated_duration_mins: number
  status: string
  approval_required: boolean
  rollback_available: boolean
  success_rate: number
  execution_count: number
  last_executed: string
  owner: string
}

interface Execution {
  id: string
  runbook_name: string
  status: string
  triggered_by: string
  executor: string
  started_at: string
  completed_at: string | null
  duration_mins: number | null
  steps_completed: number
  steps_total: number
  error_message: string | null
}

interface RunbookStats {
  total_runbooks: number
  active_runbooks: number
  total_executions: number
  running_executions: number
  success_rate: number
  failed_executions: number
  avg_execution_time_mins: number
  enabled_triggers: number
  automated_executions: number
}

const RunbookAutomationPage = () => {
  const [runbooks, setRunbooks] = useState<Runbook[]>([])
  const [executions, setExecutions] = useState<Execution[]>([])
  const [stats, setStats] = useState<RunbookStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [activeTab, setActiveTab] = useState('runbooks')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [runbooksRes, executionsRes, statsRes] = await Promise.all([
        fetch('/api/runbooks/'),
        fetch('/api/runbooks/executions'),
        fetch('/api/runbooks/statistics')
      ])

      setRunbooks(await runbooksRes.json())
      setExecutions(await executionsRes.json())
      setStats(await statsRes.json())
    } catch (error) {
      console.error('Error fetching runbook data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800'
      case 'draft': return 'bg-yellow-100 text-yellow-800'
      case 'deprecated': return 'bg-red-100 text-red-800'
      case 'completed': return 'bg-green-100 text-green-800'
      case 'running': return 'bg-blue-100 text-blue-800'
      case 'failed': return 'bg-red-100 text-red-800'
      case 'cancelled': return 'bg-gray-100 text-gray-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'incident_response': return 'bg-red-50 border-red-200'
      case 'maintenance': return 'bg-blue-50 border-blue-200'
      case 'deployment': return 'bg-purple-50 border-purple-200'
      case 'security': return 'bg-yellow-50 border-yellow-200'
      default: return 'bg-gray-50 border-gray-200'
    }
  }

  const categories = ['all', ...new Set(runbooks.map(r => r.category))]
  const filteredRunbooks = selectedCategory === 'all'
    ? runbooks
    : runbooks.filter(r => r.category === selectedCategory)

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Runbook Automation</h1>
          <p className="text-gray-600">Manage automated procedures and execution history</p>
        </div>

        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Active Runbooks</p>
              <p className="text-2xl font-bold">{stats.active_runbooks}</p>
              <p className="text-xs text-gray-500">of {stats.total_runbooks} total</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Executions</p>
              <p className="text-2xl font-bold">{stats.total_executions}</p>
              <p className="text-xs text-blue-600">{stats.running_executions} running</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Success Rate</p>
              <p className="text-2xl font-bold text-green-600">{stats.success_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Avg Time</p>
              <p className="text-2xl font-bold">{stats.avg_execution_time_mins} min</p>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-sm text-gray-500">Automated</p>
              <p className="text-2xl font-bold">{stats.automated_executions}</p>
              <p className="text-xs text-gray-500">{stats.enabled_triggers} triggers</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200">
            <nav className="flex -mb-px">
              {['runbooks', 'executions'].map((tab) => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`py-4 px-6 text-sm font-medium capitalize ${
                    activeTab === tab
                      ? 'border-b-2 border-blue-500 text-blue-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  {tab}
                </button>
              ))}
            </nav>
          </div>

          {activeTab === 'runbooks' && (
            <>
              <div className="p-4 border-b border-gray-200 flex gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-3 py-1 rounded-full text-sm capitalize ${
                      selectedCategory === cat
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {cat.replace('_', ' ')}
                  </button>
                ))}
              </div>
              <div className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredRunbooks.map((runbook) => (
                  <div key={runbook.id} className={`border rounded-lg p-4 ${getCategoryColor(runbook.category)}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="font-semibold">{runbook.name}</h3>
                        <p className="text-sm text-gray-600 mt-1">{runbook.description}</p>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(runbook.status)}`}>
                        {runbook.status}
                      </span>
                    </div>
                    <div className="mt-4 grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">Steps</p>
                        <p className="font-medium">{runbook.steps}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Duration</p>
                        <p className="font-medium">{runbook.estimated_duration_mins} min</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Success Rate</p>
                        <p className="font-medium text-green-600">{runbook.success_rate}%</p>
                      </div>
                    </div>
                    <div className="mt-4 flex justify-between items-center text-xs text-gray-500">
                      <span>Owner: {runbook.owner}</span>
                      <span>{runbook.execution_count} executions</span>
                    </div>
                    <div className="mt-2 flex gap-2">
                      {runbook.approval_required && (
                        <span className="px-2 py-1 rounded text-xs bg-yellow-100 text-yellow-800">Approval Required</span>
                      )}
                      {runbook.rollback_available && (
                        <span className="px-2 py-1 rounded text-xs bg-blue-100 text-blue-800">Rollback Available</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {activeTab === 'executions' && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Runbook</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Progress</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Triggered By</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Executor</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Started</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Duration</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {executions.slice(0, 20).map((exec) => (
                    <tr key={exec.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap font-medium">{exec.runbook_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(exec.status)}`}>
                          {exec.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div className="w-20 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-600 h-2 rounded-full"
                              style={{ width: `${(exec.steps_completed / exec.steps_total) * 100}%` }}
                            ></div>
                          </div>
                          <span className="text-xs text-gray-500">{exec.steps_completed}/{exec.steps_total}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{exec.triggered_by}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">{exec.executor}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{exec.started_at}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        {exec.duration_mins ? `${exec.duration_mins} min` : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  )
}

export default RunbookAutomationPage
